<template>
  <el-dialog
    class="batch-shipping-dialog"
    title="批量发货"
    width="1120px"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="!isBusy"
    :before-close="handleBeforeClose"
    @closed="handleClosed"
  >
    <el-alert
      type="warning"
      :closable="false"
      show-icon
      title="仅处理待发货订单。订单ID优先；未填写订单ID时，再按订单编号精确匹配。"
    ></el-alert>

    <div class="toolbar">
      <el-button
        icon="el-icon-download"
        :loading="templateLoading"
        :disabled="isBusy"
        @click="handleDownloadTemplate"
      >下载导入模板</el-button>
      <el-upload
        ref="upload"
        action=""
        accept=".xlsx,.xls,.csv"
        :auto-upload="false"
        :show-file-list="false"
        :disabled="isBusy"
        :on-change="handleFileChange"
      >
        <el-button
          type="primary"
          icon="el-icon-upload2"
          :loading="validating"
          :disabled="isBusy"
        >选择表格</el-button>
      </el-upload>
      <span v-if="fileName" class="file-name">{{ fileName }}</span>
      <span class="file-tip">支持 xlsx、xls、UTF-8 CSV，单次最多 500 条</span>
    </div>

    <div v-if="rows.length" class="summary">
      <div class="summary-item">
        <span class="label">总条数</span>
        <strong>{{ rows.length }}</strong>
      </div>
      <div class="summary-item ready">
        <span class="label">可发货</span>
        <strong>{{ summary.ready }}</strong>
      </div>
      <div class="summary-item success">
        <span class="label">成功</span>
        <strong>{{ summary.success }}</strong>
      </div>
      <div class="summary-item failed">
        <span class="label">未成功</span>
        <strong>{{ summary.notSuccess }}</strong>
      </div>
    </div>

    <div v-if="validating" class="progress-wrap">
      <p>正在校验订单 {{ validationDone }}/{{ validationTotal }}</p>
      <el-progress :percentage="validationPercentage"></el-progress>
    </div>
    <div v-if="processing" class="progress-wrap">
      <p>正在逐条发货 {{ processedCount }}/{{ queueTotal }}，请不要关闭页面</p>
      <el-progress
        :percentage="shippingPercentage"
        :status="summary.unknown ? 'exception' : undefined"
      ></el-progress>
    </div>

    <el-table
      v-if="rows.length"
      class="result-table"
      :data="rows"
      border
      height="430"
    >
      <el-table-column prop="excelRow" label="表格行" width="72" align="center"></el-table-column>
      <el-table-column prop="orderIdInput" label="订单ID" min-width="105" show-overflow-tooltip>
        <template slot-scope="scope">
          {{ scope.row.orderIdInput || scope.row.orderId || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="orderNo" label="订单编号" min-width="175" show-overflow-tooltip></el-table-column>
      <el-table-column prop="expressName" label="快递名称" min-width="120" show-overflow-tooltip></el-table-column>
      <el-table-column prop="expressNo" label="快递单号" min-width="155" show-overflow-tooltip>
        <template slot-scope="scope">
          {{ scope.row.expressNo || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="110" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="statusTagType(scope.row.status)">
            {{ statusLabel(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="reason" label="说明" min-width="230" show-overflow-tooltip>
        <template slot-scope="scope">
          {{ scope.row.reason || '-' }}
        </template>
      </el-table-column>
    </el-table>

    <div slot="footer" class="dialog-footer">
      <el-button
        v-if="rows.length && summary.notSuccess"
        :disabled="isBusy"
        @click="handleExportResults"
      >导出未成功明细</el-button>
      <el-button :disabled="isBusy" @click="dialogVisible = false">关闭</el-button>
      <el-button
        type="primary"
        :disabled="!summary.ready || isBusy || finished"
        @click="handleStartShipping"
      >开始发货（{{ summary.ready }}）</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { expressList } from '@/api/common'
import {
  memberOrderList,
  memberOrderOne,
  memberOrderShipping,
} from '@/api/member'

export default {
  name: 'BatchShipping',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      fileName: '',
      rows: [],
      expressOptions: [],
      templateLoading: false,
      validating: false,
      processing: false,
      finished: false,
      validationDone: 0,
      validationTotal: 0,
      processedCount: 0,
      queueTotal: 0,
      importUtilsPromise: null,
    }
  },
  computed: {
    isBusy() {
      return this.validating || this.processing
    },
    summary() {
      const summary = {
        ready: 0,
        success: 0,
        failed: 0,
        unknown: 0,
        notSuccess: 0,
      }
      this.rows.forEach((row) => {
        if (row.status === 'ready') summary.ready += 1
        if (row.status === 'success') summary.success += 1
        if (row.status === 'failed') summary.failed += 1
        if (row.status === 'unknown') summary.unknown += 1
        if (['invalid', 'failed', 'unknown', 'stopped'].includes(row.status)) {
          summary.notSuccess += 1
        }
      })
      return summary
    },
    validationPercentage() {
      if (!this.validationTotal) return 0
      return Math.round((this.validationDone / this.validationTotal) * 100)
    },
    shippingPercentage() {
      if (!this.queueTotal) return 0
      return Math.round((this.processedCount / this.queueTotal) * 100)
    },
  },
  watch: {
    visible(value) {
      this.dialogVisible = value
      if (value) {
        this.resetDialog()
      }
    },
  },
  methods: {
    resetDialog() {
      this.fileName = ''
      this.rows = []
      this.finished = false
      this.validationDone = 0
      this.validationTotal = 0
      this.processedCount = 0
      this.queueTotal = 0
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles()
      }
    },
    normalizeText(value) {
      if (value === null || value === undefined) return ''
      return String(value).trim()
    },
    loadImportUtils() {
      if (!this.importUtilsPromise) {
        this.importUtilsPromise = import(
          /* webpackChunkName: "order-shipping-import" */
          '@/utils/orderShippingImport'
        )
      }
      return this.importUtilsPromise
    },
    async fetchExpressOptions() {
      if (this.expressOptions.length) {
        return this.expressOptions
      }
      const result = await expressList({ pageIndex: 1, pageSize: 500 })
      if (!result || typeof result === 'string' || !Array.isArray(result.list)) {
        throw new Error(typeof result === 'string' ? result : '快递列表加载失败')
      }
      this.expressOptions = result.list
      return this.expressOptions
    },
    async handleDownloadTemplate() {
      this.templateLoading = true
      try {
        const [options, importUtils] = await Promise.all([
          this.fetchExpressOptions(),
          this.loadImportUtils(),
        ])
        importUtils.downloadShippingTemplate(options.map((item) => item.name))
      } catch (error) {
        this.$message.error(error.message || '导入模板生成失败')
      } finally {
        this.templateLoading = false
      }
    },
    async handleFileChange(file) {
      if (!file || !file.raw) return
      const fileName = file.name || ''
      const extension = fileName.includes('.')
        ? fileName.split('.').pop().toLowerCase()
        : ''
      if (!['xlsx', 'xls', 'csv'].includes(extension)) {
        this.$message.error('请选择 xlsx、xls 或 csv 格式的表格')
        this.$refs.upload.clearFiles()
        return
      }

      this.resetDialog()
      this.fileName = fileName
      this.validating = true
      try {
        const importUtils = await this.loadImportUtils()
        this.rows = await importUtils.parseShippingImportFile(file.raw)
        const options = await this.fetchExpressOptions()
        this.validateExpressNames(options)
        await this.validateOrders()
        this.markResolvedDuplicates()
        if (this.summary.ready) {
          this.$message.success(`校验完成，${this.summary.ready} 条可发货`)
        } else {
          this.$message.warning('校验完成，没有可发货的数据')
        }
      } catch (error) {
        this.rows = []
        this.$message.error(error.message || '表格导入失败')
      } finally {
        this.validating = false
        if (this.$refs.upload) {
          this.$refs.upload.clearFiles()
        }
      }
    },
    validateExpressNames(options) {
      const nameMap = options.reduce((map, item) => {
        const name = this.normalizeText(item.name)
        if (!name) return map
        if (!map[name]) map[name] = []
        map[name].push(item)
        return map
      }, {})

      this.rows.forEach((row) => {
        if (row.status === 'invalid') return
        if (row.expressName === '无需物流') {
          row.expressCode = ''
          return
        }
        const matches = nameMap[row.expressName] || []
        if (!matches.length) {
          row.status = 'invalid'
          row.reason = '快递名称与系统快递列表不匹配'
          return
        }
        if (matches.length > 1) {
          row.status = 'invalid'
          row.reason = '系统中存在多个同名快递，无法唯一匹配'
          return
        }
        row.expressCode = this.normalizeText(matches[0].code)
      })
    },
    async fetchOrder(row, forceId) {
      const queryId = forceId || row.orderIdInput
      if (queryId) {
        const result = await memberOrderOne({ id: queryId })
        if (!result || typeof result === 'string' || !result.id) {
          throw new Error(typeof result === 'string' ? result : '未查询到订单ID对应的订单')
        }
        if (row.orderNo && this.normalizeText(result.order_no) !== row.orderNo) {
          throw new Error('订单ID与订单编号不属于同一笔订单')
        }
        return result
      }

      const result = await memberOrderList({
        order_no: row.orderNo,
        pageIndex: 1,
        pageSize: 20,
        isPaging: 1,
      })
      if (!result || typeof result === 'string' || !Array.isArray(result.list)) {
        throw new Error(typeof result === 'string' ? result : '订单查询失败')
      }
      const exactOrders = result.list.filter(
        (item) => this.normalizeText(item.order_no) === row.orderNo
      )
      if (!exactOrders.length) {
        throw new Error('未查询到完全匹配的订单编号')
      }
      if (exactOrders.length > 1) {
        throw new Error('订单编号匹配到多笔订单，请改用订单ID')
      }
      return exactOrders[0]
    },
    validateOrderState(order) {
      if (Number(order.status) !== 2) {
        return `当前订单状态为“${this.orderStatusLabel(order.status)}”，不是待发货`
      }
      if (!order.receiver_address) {
        return '订单没有收货地址，不允许发货'
      }
      return ''
    },
    async validateOrders() {
      const candidates = this.rows.filter((row) => row.status === 'pending')
      this.validationDone = 0
      this.validationTotal = candidates.length
      let cursor = 0
      const worker = async () => {
        while (cursor < candidates.length) {
          const row = candidates[cursor]
          cursor += 1
          row.status = 'checking'
          try {
            const order = await this.fetchOrder(row)
            row.orderId = order.id
            if (!row.orderNo) {
              row.orderNo = this.normalizeText(order.order_no)
            }
            const stateError = this.validateOrderState(order)
            if (stateError) {
              row.status = 'invalid'
              row.reason = stateError
            } else {
              row.status = 'ready'
              row.reason = ''
            }
          } catch (error) {
            row.status = 'invalid'
            row.reason = error.message || '订单校验失败'
          } finally {
            this.validationDone += 1
          }
        }
      }
      const workers = []
      const workerCount = Math.min(3, candidates.length)
      for (let index = 0; index < workerCount; index += 1) {
        workers.push(worker())
      }
      await Promise.all(workers)
    },
    markResolvedDuplicates() {
      const idMap = this.rows.reduce((map, row) => {
        if (row.status === 'ready' && row.orderId !== null) {
          const key = String(row.orderId)
          if (!map[key]) map[key] = []
          map[key].push(row)
        }
        return map
      }, {})
      Object.keys(idMap).forEach((key) => {
        if (idMap[key].length > 1) {
          idMap[key].forEach((row) => {
            row.status = 'invalid'
            row.reason = '多行数据最终指向同一笔订单'
          })
        }
      })
    },
    async handleStartShipping() {
      const readyCount = this.summary.ready
      if (!readyCount) return
      try {
        await this.$confirm(
          `即将逐条提交 ${readyCount} 笔待发货订单。已成功的订单无法批量回滚，是否继续？`,
          '确认批量发货',
          {
            type: 'warning',
            confirmButtonText: '开始发货',
            cancelButtonText: '取消',
          }
        )
      } catch (error) {
        return
      }

      const queue = this.rows.filter((row) => row.status === 'ready')
      this.processing = true
      this.finished = false
      this.processedCount = 0
      this.queueTotal = queue.length
      let shouldStop = false

      for (let index = 0; index < queue.length; index += 1) {
        const row = queue[index]
        if (shouldStop) {
          row.status = 'stopped'
          row.reason = '队列因上一条结果不明确而停止，未调用发货接口'
          this.processedCount += 1
          continue
        }

        row.status = 'processing'
        row.reason = ''
        try {
          const latestOrder = await this.fetchOrder(row, row.orderId)
          const stateError = this.validateOrderState(latestOrder)
          if (stateError) {
            row.status = 'failed'
            row.reason = `提交前校验未通过：${stateError}`
            this.processedCount += 1
            continue
          }

          const orderIdentifier = row.orderIdInput
            ? { id: row.orderIdInput }
            : { order_no: row.orderNo }
          const result = await memberOrderShipping({
            ...orderIdentifier,
            express_code: row.expressCode,
            express_no: row.expressCode ? row.expressNo : '',
          })

          if (typeof result === 'string' && result.trim()) {
            row.status = 'failed'
            row.reason = result
          } else if (result === undefined || result === null) {
            const verified = await this.verifyShippingResult(row)
            if (verified) {
              row.status = 'success'
              row.reason = '发货成功（已回查订单）'
            } else {
              row.status = 'unknown'
              row.reason = '接口未返回明确结果，回查也未能确认，请人工核对'
              shouldStop = true
            }
          } else {
            row.status = 'success'
            row.reason = '发货成功'
          }
        } catch (error) {
          const verified = await this.verifyShippingResult(row)
          if (verified) {
            row.status = 'success'
            row.reason = '请求返回异常，但已回查确认发货成功'
          } else {
            row.status = 'unknown'
            row.reason = `${error.message || '网络请求异常'}；为避免重复发货，队列已停止`
            shouldStop = true
          }
        }
        this.processedCount += 1
      }

      this.processing = false
      this.finished = true
      this.$emit('completed')
      if (this.summary.unknown) {
        this.$message.warning('队列已停止，存在结果待确认的订单')
      } else if (this.summary.notSuccess) {
        this.$message.warning(`批量发货完成：成功 ${this.summary.success} 条，未成功 ${this.summary.notSuccess} 条`)
      } else {
        this.$message.success(`批量发货完成，成功 ${this.summary.success} 条`)
      }
    },
    async verifyShippingResult(row) {
      try {
        const order = await this.fetchOrder(row, row.orderId)
        const statusConfirmed = [3, 4].includes(Number(order.status))
        const codeConfirmed = this.normalizeText(order.express_code) === row.expressCode
        const noConfirmed = row.expressCode
          ? this.normalizeText(order.express_no) === row.expressNo
          : true
        return statusConfirmed && codeConfirmed && noConfirmed
      } catch (error) {
        return false
      }
    },
    async handleExportResults() {
      try {
        const importUtils = await this.loadImportUtils()
        const exported = importUtils.downloadShippingResults(this.rows)
        if (!exported) {
          this.$message.info('没有可导出的未成功数据')
        }
      } catch (error) {
        this.$message.error(error.message || '导出失败')
      }
    },
    orderStatusLabel(status) {
      const labels = {
        0: '待支付',
        1: '已支付',
        2: '待发货',
        3: '待收货',
        4: '已完成',
        5: '已取消',
      }
      return labels[Number(status)] || `未知状态(${status})`
    },
    statusLabel(status) {
      const labels = {
        pending: '待校验',
        checking: '校验中',
        invalid: '校验不通过',
        ready: '可发货',
        processing: '发货中',
        success: '成功',
        failed: '失败',
        unknown: '结果待确认',
        stopped: '未处理',
      }
      return labels[status] || status
    },
    statusTagType(status) {
      const types = {
        invalid: 'danger',
        ready: 'warning',
        processing: '',
        success: 'success',
        failed: 'danger',
        unknown: 'danger',
        stopped: 'info',
        checking: '',
        pending: 'info',
      }
      return types[status] || 'info'
    },
    handleBeforeClose(done) {
      if (this.isBusy) {
        this.$message.warning('当前任务正在执行，暂时不能关闭')
        return
      }
      done()
    },
    handleClosed() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="less" scoped>
.batch-shipping-dialog {
  .toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 18px 0;

    .file-name {
      max-width: 260px;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-tip {
      color: #909399;
      font-size: 13px;
    }
  }

  .summary {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;

    .summary-item {
      min-width: 118px;
      padding: 10px 14px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #f8f9fb;

      .label {
        margin-right: 10px;
        color: #909399;
      }

      strong {
        color: #303133;
        font-size: 18px;
      }

      &.ready strong {
        color: #e6a23c;
      }

      &.success strong {
        color: #67c23a;
      }

      &.failed strong {
        color: #f56c6c;
      }
    }
  }

  .progress-wrap {
    margin-bottom: 16px;

    p {
      margin: 0 0 8px;
      color: #606266;
    }
  }

  .result-table {
    width: 100%;
  }
}
</style>
